import React, { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

// ─── Parameters ──────────────────────────────────────────────────────────────
//
//  The galaxy uses two separate point-cloud layers so we can vary particle
//  size independently — something a single <points> can't do:
//
//  Layer 1 — "stars"   small (size 2.0), high count, sharp definition
//  Layer 2 — "nebula"  large (size 9),   low count,  soft glow patches
//
//  Structure (Layer 1):
//    4 logarithmic spiral arms  ×  16 000 pts each  =  64 000
//    Central bar (barred spiral)                    =   3 000
//    Spheroidal bulge                               =   9 000
//    Background disc haze                           =   4 000
//    Total Layer 1                                  =  80 000
//
//  Structure (Layer 2 — nebula):
//    4 arm nebula patches  ×  800 pts               =   3 200
//    Bulge glow                                     =     600
//    Total Layer 2                                  =   3 800
//
// Logarithmic spiral:  r = R0 · exp(B · θ)
//   R0 = 240,  B = 0.175,  θ ∈ [0, 4π]  → r ∈ [240, 2150]
//
// Fade-in: starts at camera distance 380, fully opaque at 760
// (requires roughly two full scroll-wheel zooms past the solar system)
//
const R0   = 240;
const B    = 0.175;
const TMAX = 4 * Math.PI;   // 2 full turns per arm

const ARMS          = 4;
const ARM_PTS       = 16000;
const BAR_PTS       = 3000;
const BULGE_PTS     = 9000;
const HAZE_PTS      = 4000;
const TOTAL_STARS   = ARMS * ARM_PTS + BAR_PTS + BULGE_PTS + HAZE_PTS;

const ARM_NEB_PTS   = 800;
const BULGE_NEB_PTS = 600;
const TOTAL_NEBULA  = ARMS * ARM_NEB_PTS + BULGE_NEB_PTS;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const lerp    = (a, b, t) => a + (b - a) * t;
const rand    = () => Math.random() - 0.5;   // [-0.5, 0.5]

const MilkyWay = () => {
  const { camera }  = useThree();
  const starsMatRef  = useRef();
  const nebMatRef    = useRef();
  const groupRef     = useRef();

  // ── Layer 1 — star geometry ───────────────────────────────────────────────
  const starsGeo = useMemo(() => {
    const pos = new Float32Array(TOTAL_STARS * 3);
    const col = new Float32Array(TOTAL_STARS * 3);
    let i = 0;

    // ── Spiral arms ─────────────────────────────────────────────────────────
    for (let arm = 0; arm < ARMS; arm++) {
      const phase = (arm / ARMS) * Math.PI * 2;

      for (let p = 0; p < ARM_PTS; p++) {
        const t     = p / ARM_PTS;
        const theta = t * TMAX;
        const r     = R0 * Math.exp(B * theta);

        // Scatter: tight at inner edge, opening up toward outer edge
        const sR   = 10 + r * 0.045;   // radial ± spread
        const sAng = 0.03 + t * 0.05;  // angular ± spread (radians)
        const sY   = 2  + r * 0.012;   // vertical ±  (very thin disc)

        const aa = theta + phase + rand() * sAng * 2;
        const rr = r     + rand() * sR * 2;

        pos[i * 3]     = Math.cos(aa) * rr;
        pos[i * 3 + 1] = rand() * sY * 2;
        pos[i * 3 + 2] = Math.sin(aa) * rr;

        // Color: warm yellow-white → cool blue-purple along arm
        // Use exponential decay for a realistic stellar-population gradient
        const warm   = Math.exp(-t * 1.8);
        const bright = 0.55 + warm * 0.45;
        col[i * 3]     = bright * lerp(0.52, 0.95, warm);  // R
        col[i * 3 + 1] = bright * lerp(0.56, 0.88, warm);  // G
        col[i * 3 + 2] = bright * lerp(0.98, 0.60, warm);  // B
        i++;
      }
    }

    // ── Central bar ─────────────────────────────────────────────────────────
    // A barred-spiral bar: dense elongated structure along one axis
    for (let p = 0; p < BAR_PTS; p++) {
      const along = rand() * 2 * 320;   // ±320 along bar axis
      const perp  = rand() * 2 * 45;    // ±45 perpendicular
      const vert  = rand() * 2 * 18;    // ±18 vertical

      pos[i * 3]     = along;
      pos[i * 3 + 1] = vert;
      pos[i * 3 + 2] = perp;

      const t = Math.abs(along) / 320;
      const br = 0.85 + (1 - t) * 0.15;
      col[i * 3]     = br * 1.00;
      col[i * 3 + 1] = br * 0.88;
      col[i * 3 + 2] = br * 0.50;
      i++;
    }

    // ── Bulge ────────────────────────────────────────────────────────────────
    // Inverse-square distribution for a compact, bright core
    for (let p = 0; p < BULGE_PTS; p++) {
      const r     = Math.pow(Math.random(), 1.8) * 420;
      const theta = Math.random() * Math.PI * 2;
      const phi   = rand() * 0.55;      // ±~16° flattened

      pos[i * 3]     = Math.cos(theta) * r * Math.cos(phi);
      pos[i * 3 + 1] = Math.sin(phi) * r * 0.35;
      pos[i * 3 + 2] = Math.sin(theta) * r * Math.cos(phi);

      const t  = r / 420;
      const br = 0.88 + (1 - t) * 0.12;
      col[i * 3]     = br * 1.00;
      col[i * 3 + 1] = br * 0.87;
      col[i * 3 + 2] = br * 0.48;
      i++;
    }

    // ── Background haze disc ─────────────────────────────────────────────────
    for (let p = 0; p < HAZE_PTS; p++) {
      const r     = 280 + Math.random() * 1600;
      const theta = Math.random() * Math.PI * 2;
      const y     = rand() * (30 + r * 0.035) * 2;

      pos[i * 3]     = Math.cos(theta) * r;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = Math.sin(theta) * r;

      // faint blue-purple inter-arm scatter
      const t = (r - 280) / 1600;
      col[i * 3]     = 0.38 + (1 - t) * 0.18;
      col[i * 3 + 1] = 0.38 + (1 - t) * 0.12;
      col[i * 3 + 2] = 0.72 + (1 - t) * 0.14;
      i++;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("color",    new THREE.BufferAttribute(col, 3));
    return geo;
  }, []);

  // ── Layer 2 — nebula / glow patches ──────────────────────────────────────
  const nebGeo = useMemo(() => {
    const pos = new Float32Array(TOTAL_NEBULA * 3);
    const col = new Float32Array(TOTAL_NEBULA * 3);
    let i = 0;

    // Arm glow patches — large, soft, colorful
    for (let arm = 0; arm < ARMS; arm++) {
      const phase = (arm / ARMS) * Math.PI * 2;

      for (let p = 0; p < ARM_NEB_PTS; p++) {
        const t     = p / ARM_NEB_PTS;
        const theta = t * TMAX;
        const r     = R0 * Math.exp(B * theta);

        const aa = theta + phase + rand() * 0.18;
        const rr = r + rand() * r * 0.12;

        pos[i * 3]     = Math.cos(aa) * rr;
        pos[i * 3 + 1] = rand() * 24;
        pos[i * 3 + 2] = Math.sin(aa) * rr;

        // Nebula colors: pink-magenta, blue, purple patches
        const which = Math.floor(Math.random() * 3);
        if (which === 0) { col[i*3]=0.9; col[i*3+1]=0.4; col[i*3+2]=0.7; }  // pink
        else if (which === 1) { col[i*3]=0.3; col[i*3+1]=0.55; col[i*3+2]=1.0; }  // blue
        else { col[i*3]=0.6; col[i*3+1]=0.3; col[i*3+2]=0.95; }  // purple
        i++;
      }
    }

    // Bulge glow patches — warm orange-yellow
    for (let p = 0; p < BULGE_NEB_PTS; p++) {
      const r     = Math.random() * 350;
      const theta = Math.random() * Math.PI * 2;
      const phi   = rand() * 0.3;

      pos[i * 3]     = Math.cos(theta) * r * Math.cos(phi);
      pos[i * 3 + 1] = Math.sin(phi) * r * 0.3;
      pos[i * 3 + 2] = Math.sin(theta) * r * Math.cos(phi);

      col[i * 3]     = 1.0;
      col[i * 3 + 1] = 0.72;
      col[i * 3 + 2] = 0.22;
      i++;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("color",    new THREE.BufferAttribute(col, 3));
    return geo;
  }, []);

  // ── Animate ───────────────────────────────────────────────────────────────
  useFrame(() => {
    if (!starsMatRef.current || !nebMatRef.current || !groupRef.current) return;

    const dist = camera.position.length();

    // Fade in: starts at 380, fully visible at 760
    // (requires zooming out well past the solar system)
    let opacity = 0;
    if (dist > 380 && dist < 760) {
      opacity = (dist - 380) / 380;
    } else if (dist >= 760) {
      opacity = 1;
    }
    const op = Math.min(opacity, 1);

    starsMatRef.current.opacity = op;
    nebMatRef.current.opacity   = op * 0.28;   // nebula is always subtler
    groupRef.current.visible    = dist > 360;

    // Imperceptibly slow rotation
    groupRef.current.rotation.y += 0.000012;
  });

  return (
    // ~22° tilt from the ecliptic plane — shows the disc in 3D perspective
    <group ref={groupRef} rotation={[0.38, 0.55, 0]}>

      {/* Layer 1 — fine star points */}
      <points geometry={starsGeo}>
        <pointsMaterial
          ref={starsMatRef}
          size={2.0}
          vertexColors
          transparent
          opacity={0}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          sizeAttenuation
        />
      </points>

      {/* Layer 2 — soft nebula glow patches */}
      <points geometry={nebGeo}>
        <pointsMaterial
          ref={nebMatRef}
          size={9}
          vertexColors
          transparent
          opacity={0}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          sizeAttenuation
        />
      </points>

    </group>
  );
};

export default MilkyWay;
